import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { 
  X, 
  Rotate3d, 
  Sparkles, 
  Play, 
  Pause, 
  Layers,
  Sliders,
  Info,
  RefreshCw,
  Zap,
  Flame,
  Wand2,
  Cpu,
  CheckCircle2,
  SlidersHorizontal,
  ChevronRight,
  ShieldCheck,
  Compass
} from 'lucide-react';

interface ThreeDViewerProps {
  imageUrl: string;
  title: string;
  itemCode?: string;
  onClose: () => void;
}

export default function ThreeDViewer({ imageUrl, title, itemCode, onClose }: ThreeDViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Custom Studio States
  const [bgThreshold, setBgThreshold] = useState<number>(45); // Background removal sensitivity
  const [depthScale, setDepthScale] = useState<number>(0.65); // Displacement scale (grosor)
  const [normalStrength, setNormalStrength] = useState<number>(2.5); // Sobel normal map intensity
  const [invertRelief, setInvertRelief] = useState<boolean>(false);
  const [metalness, setMetalness] = useState<number>(0.92);
  const [roughness, setRoughness] = useState<number>(0.18);
  const [isProcessing, setIsProcessing] = useState<boolean>(true);
  
  // AI Reconstruction Pipeline Simulation States
  const [aiStep, setAiStep] = useState<number>(0);
  const [aiProgress, setAiProgress] = useState<number>(0);
  const [aiStatusText, setAiStatusText] = useState<string>('Iniciando Red Neuronal...');
  const [pipelineFinished, setPipelineFinished] = useState<boolean>(false);

  const [rotationSpeed, setRotationSpeed] = useState<number>(0.005);
  const [isAutoRotating, setIsAutoRotating] = useState<boolean>(true);
  const [wireframe, setWireframe] = useState<boolean>(false);
  const [gridVisible, setGridVisible] = useState<boolean>(true);
  const [materialPreset, setMaterialPreset] = useState<'steel' | 'gold' | 'hologram'>('steel');
  const [laserActive, setLaserActive] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'material' | 'studio' | 'metadata'>('material');

  // Interactive telemetry
  const [yaw, setYaw] = useState<number>(0);
  const [pitch, setPitch] = useState<number>(0);
  const [zoomLevel, setZoomLevel] = useState<number>(100);

  // References to dynamically update ThreeJS objects without restarting the full render loop
  const materialRef = useRef<THREE.MeshStandardMaterial | null>(null);
  const backMaterialRef = useRef<THREE.MeshStandardMaterial | null>(null);
  const wireframeMeshRef = useRef<THREE.Mesh | null>(null);
  const gridHelperRef = useRef<THREE.GridHelper | null>(null);
  const laserMeshRef = useRef<THREE.Mesh | null>(null);
  const pointLightRef = useRef<THREE.PointLight | null>(null);

  // Triggers to regenerate textures when processing-heavy parameters change
  const [textureKey, setTextureKey] = useState<number>(0);
  const [processedTextures, setProcessedTextures] = useState<{ 
    colorDataUrl: string; 
    depthDataUrl: string;
    normalDataUrl: string;
  } | null>(null);

  // --- Image Processing & Normal Map Generation Algorithm ---
  // Processes raw 2D image client-side to strip backgrounds, smooth edges and extract physical depth + Sobel normals
  useEffect(() => {
    let active = true;
    setPipelineFinished(false);

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;

    img.onload = () => {
      if (!active) return;
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      // Maintain good detail but scale down if too huge to keep GPU/canvas blazing fast
      const maxDim = 512;
      let w = img.naturalWidth || img.width;
      let h = img.naturalHeight || img.height;
      if (w > maxDim || h > maxDim) {
        if (w > h) {
          h = Math.round((h * maxDim) / w);
          w = maxDim;
        } else {
          w = Math.round((w * maxDim) / h);
          h = maxDim;
        }
      }

      canvas.width = w;
      canvas.height = h;

      // Draw original image
      ctx.drawImage(img, 0, 0, w, h);
      const imgData = ctx.getImageData(0, 0, w, h);
      const data = imgData.data;

      // 1. Multi-point corner background detection
      // Samples corners to accurately estimate background tone (even with slight lighting gradients)
      const corners = [
        { r: data[0], g: data[1], b: data[2] }, // top-left
        { r: data[(w - 1) * 4], g: data[(w - 1) * 4 + 1], b: data[(w - 1) * 4 + 2] }, // top-right
        { r: data[(h - 1) * w * 4], g: data[(h - 1) * w * 4 + 1], b: data[(h - 1) * w * 4 + 2] }, // bottom-left
        { r: data[(h * w - 1) * 4], g: data[(h * w - 1) * 4 + 1], b: data[(h * w - 1) * 4 + 2] } // bottom-right
      ];

      // Average corner values
      const bgR = Math.round(corners.reduce((sum, c) => sum + c.r, 0) / 4);
      const bgG = Math.round(corners.reduce((sum, c) => sum + c.g, 0) / 4);
      const bgB = Math.round(corners.reduce((sum, c) => sum + c.b, 0) / 4);

      // Create separate canvases for transparent color texture and depth grayscale heightmap
      const colorCanvas = document.createElement('canvas');
      colorCanvas.width = w;
      colorCanvas.height = h;
      const colorCtx = colorCanvas.getContext('2d')!;
      const colorImgData = colorCtx.createImageData(w, h);
      const colorData = colorImgData.data;

      const depthCanvas = document.createElement('canvas');
      depthCanvas.width = w;
      depthCanvas.height = h;
      const depthCtx = depthCanvas.getContext('2d')!;
      const depthImgData = depthCtx.createImageData(w, h);
      const depthData = depthImgData.data;

      // 2. Core segmentation and feathering loop
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];

        // Euclidean color distance from background
        const distance = Math.sqrt(
          Math.pow(r - bgR, 2) + 
          Math.pow(g - bgG, 2) + 
          Math.pow(b - bgB, 2)
        );

        const transitionWidth = 16; // Edge-feathering pixel zone

        if (distance < bgThreshold || a < 30) {
          // Transparent Background Color Map
          colorData[i] = 0;
          colorData[i + 1] = 0;
          colorData[i + 2] = 0;
          colorData[i + 3] = 0; // Solid transparent

          // Zero depth level for heightmap
          depthData[i] = 0;
          depthData[i + 1] = 0;
          depthData[i + 2] = 0;
          depthData[i + 3] = 0;
        } else if (distance < bgThreshold + transitionWidth) {
          // Soft feathered transition edge
          const ratio = (distance - bgThreshold) / transitionWidth; // Smooth 0 to 1
          const alphaVal = Math.round(ratio * 255);

          colorData[i] = r;
          colorData[i + 1] = g;
          colorData[i + 2] = b;
          colorData[i + 3] = alphaVal;

          let luma = 0.299 * r + 0.587 * g + 0.114 * b;
          if (invertRelief) {
            luma = 255 - luma;
          }

          // Height tapers off smoothly at the borders to eliminate jagged spikes
          const depthVal = Math.round((50 + (luma / 255) * 205) * ratio);
          depthData[i] = depthVal;
          depthData[i + 1] = depthVal;
          depthData[i + 2] = depthVal;
          depthData[i + 3] = alphaVal;
        } else {
          // Solid product body
          colorData[i] = r;
          colorData[i + 1] = g;
          colorData[i + 2] = b;
          colorData[i + 3] = 255;

          // Convert to grayscale for height map displacement
          let luma = 0.299 * r + 0.587 * g + 0.114 * b;
          if (invertRelief) {
            luma = 255 - luma;
          }

          const depthVal = Math.round(50 + (luma / 255) * 205);
          depthData[i] = depthVal;
          depthData[i + 1] = depthVal;
          depthData[i + 2] = depthVal;
          depthData[i + 3] = 255;
        }
      }

      colorCtx.putImageData(colorImgData, 0, 0);
      depthCtx.putImageData(depthImgData, 0, 0);

      // 3. Real-time Sobel Normal Map generator
      const normalCanvas = document.createElement('canvas');
      normalCanvas.width = w;
      normalCanvas.height = h;
      const normalCtx = normalCanvas.getContext('2d')!;
      const normalImgData = normalCtx.createImageData(w, h);
      const normalData = normalImgData.data;

      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const idx = (y * w + x) * 4;

          // Sobel central differences for heights
          const xLeft  = depthData[(y * w + Math.max(0, x - 1)) * 4];
          const xRight = depthData[(y * w + Math.min(w - 1, x + 1)) * 4];
          const yUp    = depthData[(Math.max(0, y - 1) * w + x) * 4];
          const yDown  = depthData[(Math.min(h - 1, y + 1) * w + x) * 4];

          // Compute slope/gradients
          const dx = ((xRight - xLeft) / 255.0) * normalStrength;
          const dy = ((yDown - yUp) / 255.0) * normalStrength;

          // Normalize normal vector: n = normalize(-dx, -dy, 1.0)
          const len = Math.sqrt(dx * dx + dy * dy + 1.0);
          const nx = -dx / len;
          const ny = -dy / len;
          const nz = 1.0 / len;

          // Map vector coordinates from [-1, 1] to RGB [0, 255]
          normalData[idx]     = Math.round((nx * 0.5 + 0.5) * 255);     // Red (X)
          normalData[idx + 1] = Math.round((ny * 0.5 + 0.5) * 255);     // Green (Y)
          normalData[idx + 2] = Math.round((nz * 0.5 + 0.5) * 255);     // Blue (Z)
          normalData[idx + 3] = depthData[idx + 3];                     // Align alpha channel
        }
      }

      normalCtx.putImageData(normalImgData, 0, 0);

      if (active) {
        setProcessedTextures({
          colorDataUrl: colorCanvas.toDataURL('image/png'),
          depthDataUrl: depthCanvas.toDataURL('image/png'),
          normalDataUrl: normalCanvas.toDataURL('image/png')
        });
      }
    };

    img.onerror = () => {
      if (active) {
        console.error("Error al procesar el material 3D.");
        setIsProcessing(false);
      }
    };

    return () => {
      active = false;
    };
  }, [imageUrl, bgThreshold, invertRelief, normalStrength, textureKey]);

  // --- AI Reconstruction Pipeline Simulation ---
  useEffect(() => {
    if (!processedTextures) return;
    
    setIsProcessing(true);
    setAiProgress(0);
    setAiStep(0);
    setPipelineFinished(false);
    
    const steps = [
      { text: 'Sementación neuronal: Extrayendo silueta del producto...' },
      { text: 'Análisis volumétrico: Generando mapa de alturas (Luma Heightmap)...' },
      { text: 'Modelado de superficie: Sintetizando textura Sobel de alta fidelidad...' },
      { text: 'Acelerador WebGL: Ensamblando malla tridimensional (73k caras)...' }
    ];
    
    let currentProgress = 0;
    const interval = setInterval(() => {
      currentProgress += 2;
      if (currentProgress > 100) currentProgress = 100;
      
      setAiProgress(currentProgress);
      
      const stepIdx = Math.min(3, Math.floor(currentProgress / 25));
      setAiStep(stepIdx);
      setAiStatusText(steps[stepIdx].text);
      
      if (currentProgress === 100) {
        clearInterval(interval);
        setTimeout(() => {
          setIsProcessing(false);
          setPipelineFinished(true);
        }, 400);
      }
    }, 25); // ~1.2s total processing time for dynamic excitement
    
    return () => clearInterval(interval);
  }, [processedTextures]);

  // --- Real-time WebGL Renderer Loop ---
  useEffect(() => {
    if (!containerRef.current || !processedTextures || isProcessing) return;

    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;

    // --- 1. Scene & Environment Setup ---
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x020617); // Slate-950

    // --- 2. Perspective Camera with Depth ---
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    camera.position.set(0, 1.6, 5.8);

    // --- 3. WebGL Renderer with Shadows ---
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: "high-performance" });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    containerRef.current.appendChild(renderer.domElement);

    // --- 4. High-Contrast Studio Lights ---
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.2);
    scene.add(ambientLight);

    const mainLight = new THREE.DirectionalLight(0xffffff, 1.8);
    mainLight.position.set(4, 7, 4);
    mainLight.castShadow = true;
    mainLight.shadow.mapSize.width = 2048;
    mainLight.shadow.mapSize.height = 2048;
    mainLight.shadow.bias = -0.0005;
    scene.add(mainLight);

    const rimLight = new THREE.DirectionalLight(0x06b6d4, 1.4); // Cyber Rim light
    rimLight.position.set(-5, -2, -4);
    scene.add(rimLight);

    const pointLight = new THREE.PointLight(0x06b6d4, 1.5, 12); // Dynamic core blue light
    pointLight.position.set(0, 0, 1.5);
    scene.add(pointLight);
    pointLightRef.current = pointLight;

    // --- 5. Holographic Laser Grid ---
    const gridHelper = new THREE.GridHelper(10, 20, 0x06b6d4, 0x1e293b);
    gridHelper.position.y = -2.2;
    scene.add(gridHelper);
    gridHelperRef.current = gridHelper;

    // --- 6. Creating Extruded-Look Texture Maps ---
    const textureLoader = new THREE.TextureLoader();
    
    const colorTex = textureLoader.load(processedTextures.colorDataUrl);
    colorTex.colorSpace = THREE.SRGBColorSpace;
    colorTex.minFilter = THREE.LinearFilter;

    const depthTex = textureLoader.load(processedTextures.depthDataUrl);
    depthTex.minFilter = THREE.LinearFilter;

    const normalTex = textureLoader.load(processedTextures.normalDataUrl);
    normalTex.minFilter = THREE.LinearFilter;

    // Create high-density Plane Geometry to allow displacement map to form amazing 3D volume
    const geometry = new THREE.PlaneGeometry(3.2, 3.2, 192, 192);

    // Create highly advanced standard material with alpha cutouts
    const material = new THREE.MeshStandardMaterial({
      map: colorTex,
      displacementMap: depthTex,
      displacementScale: depthScale,
      bumpMap: depthTex,
      bumpScale: 0.02,
      normalMap: normalTex,
      normalScale: new THREE.Vector2(1.8, 1.8),
      metalness: metalness,
      roughness: roughness,
      alphaTest: 0.35, // Discards transparent background pixels
      side: THREE.DoubleSide,
      shadowSide: THREE.DoubleSide
    });

    materialRef.current = material;

    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    scene.add(mesh);

    // --- 7. Solid Backing Plate (Gives real physical 3D thickness) ---
    const backGeom = new THREE.BoxGeometry(3.18, 3.18, 0.16);
    
    let backColor = 0x1e293b; 
    let backMetalness = 0.85;
    let backRoughness = 0.25;
    let backOpacity = 1.0;
    let backTransparent = false;

    if (materialPreset === 'gold') {
      backColor = 0x78350f;
      backMetalness = 0.95;
      backRoughness = 0.2;
    } else if (materialPreset === 'hologram') {
      backColor = 0x0891b2;
      backMetalness = 0.1;
      backRoughness = 0.1;
      backOpacity = 0.35;
      backTransparent = true;
    }

    const backMat = new THREE.MeshStandardMaterial({
      color: backColor,
      metalness: backMetalness,
      roughness: backRoughness,
      transparent: backTransparent,
      opacity: backOpacity
    });

    backMaterialRef.current = backMat;

    const backMesh = new THREE.Mesh(backGeom, backMat);
    backMesh.position.z = -0.081; // Center of back plate aligned behind front displaced plane
    mesh.add(backMesh);

    // --- 8. Outer Cyber Wireframe following displacement ---
    const wireframeGeom = new THREE.PlaneGeometry(3.24, 3.24, 64, 64);
    const wireframeMat = new THREE.MeshBasicMaterial({
      color: 0x06b6d4,
      wireframe: true,
      transparent: true,
      opacity: 0.12,
      depthWrite: false
    });
    const wireframeMesh = new THREE.Mesh(wireframeGeom, wireframeMat);
    wireframeMesh.position.z = 0.01;
    mesh.add(wireframeMesh);
    wireframeMeshRef.current = wireframeMesh;

    // --- 9. Cyber Laser Scanner Plane ---
    const laserGeom = new THREE.BoxGeometry(3.6, 0.03, 0.2);
    const laserMat = new THREE.MeshBasicMaterial({
      color: 0x22d3ee, // Bright cyan
      transparent: true,
      opacity: 0.7
    });
    const laserMesh = new THREE.Mesh(laserGeom, laserMat);
    laserMesh.position.y = 0;
    scene.add(laserMesh);
    laserMeshRef.current = laserMesh;

    // --- 10. Orbit Interaction Controller ---
    let isDragging = false;
    let prevMouseX = 0;
    let prevMouseY = 0;
    let targetRotationX = 0.15;
    let targetRotationY = -0.4;

    const onMouseDown = (e: MouseEvent) => {
      isDragging = true;
      prevMouseX = e.clientX;
      prevMouseY = e.clientY;
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const deltaX = e.clientX - prevMouseX;
      const deltaY = e.clientY - prevMouseY;
      
      targetRotationY += deltaX * 0.007;
      targetRotationX += deltaY * 0.007;

      // Restrict pitch angles
      targetRotationX = Math.max(-Math.PI / 3, Math.min(Math.PI / 3, targetRotationX));

      prevMouseX = e.clientX;
      prevMouseY = e.clientY;
    };

    const onMouseUp = () => {
      isDragging = false;
    };

    // Touch Support for Mobile
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        isDragging = true;
        prevMouseX = e.touches[0].clientX;
        prevMouseY = e.touches[0].clientY;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!isDragging || e.touches.length !== 1) return;
      const deltaX = e.touches[0].clientX - prevMouseX;
      const deltaY = e.touches[0].clientY - prevMouseY;

      targetRotationY += deltaX * 0.007;
      targetRotationX += deltaY * 0.007;
      targetRotationX = Math.max(-Math.PI / 3, Math.min(Math.PI / 3, targetRotationX));

      prevMouseX = e.touches[0].clientX;
      prevMouseY = e.touches[0].clientY;
    };

    const onTouchEnd = () => {
      isDragging = false;
    };

    const dom = renderer.domElement;
    dom.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    
    dom.addEventListener('touchstart', onTouchStart, { passive: true });
    dom.addEventListener('touchmove', onTouchMove, { passive: true });
    dom.addEventListener('touchend', onTouchEnd);

    // Zoom on wheel scroll
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      camera.position.z += e.deltaY * 0.0045;
      camera.position.z = Math.max(2.5, Math.min(10, camera.position.z));
      const percentage = Math.round((5.8 / camera.position.z) * 100);
      setZoomLevel(percentage);
    };
    dom.addEventListener('wheel', onWheel, { passive: false });

    // --- 11. Real-time Frame Loop ---
    let animationFrameId: number;
    let laserDirection = 1;
    let clock = new THREE.Clock();

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      // Auto Rotation logic
      if (isAutoRotating && !isDragging) {
        targetRotationY += rotationSpeed;
      }

      // Smooth interpolation (lerp)
      mesh.rotation.y += (targetRotationY - mesh.rotation.y) * 0.08;
      mesh.rotation.x += (targetRotationX - mesh.rotation.x) * 0.08;

      // Laser bounce simulation
      if (laserActive && laserMeshRef.current) {
        laserMeshRef.current.position.y += laserDirection * 0.022;
        if (laserMeshRef.current.position.y > 1.8) {
          laserDirection = -1;
        } else if (laserMeshRef.current.position.y < -1.8) {
          laserDirection = 1;
        }
      }

      // Dynamic light glowing
      const elapsedTime = clock.getElapsedTime();
      if (pointLightRef.current) {
        pointLightRef.current.intensity = 1.2 + Math.sin(elapsedTime * 3.0) * 0.4;
      }

      // Update interactive stats
      setYaw(Math.round(mesh.rotation.y * (180 / Math.PI)) % 360);
      setPitch(Math.round(mesh.rotation.x * (180 / Math.PI)) % 360);

      renderer.render(scene, camera);
    };

    animate();

    // --- 12. Viewport Resizing ---
    const handleResize = () => {
      if (!containerRef.current) return;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    // --- Clean resources on Unmount ---
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      dom.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      dom.removeEventListener('touchstart', onTouchStart);
      dom.removeEventListener('touchmove', onTouchMove);
      dom.removeEventListener('touchend', onTouchEnd);
      dom.removeEventListener('wheel', onWheel);
      
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      
      geometry.dispose();
      material.dispose();
      backGeom.dispose();
      backMat.dispose();
      colorTex.dispose();
      depthTex.dispose();
      normalTex.dispose();
      wireframeGeom.dispose();
      wireframeMat.dispose();
      laserGeom.dispose();
      laserMat.dispose();
      renderer.dispose();
    };
  }, [processedTextures, isProcessing, rotationSpeed, isAutoRotating, laserActive]);

  // Real-time material preset modifier
  useEffect(() => {
    if (!materialRef.current) return;
    
    if (materialPreset === 'steel') {
      materialRef.current.color.setHex(0xffffff);
      setMetalness(0.92);
      setRoughness(0.18);
      if (pointLightRef.current) pointLightRef.current.color.setHex(0x06b6d4); // Cyan laser
      if (backMaterialRef.current) {
        backMaterialRef.current.color.setHex(0x1e293b);
        backMaterialRef.current.metalness = 0.85;
        backMaterialRef.current.roughness = 0.25;
        backMaterialRef.current.transparent = false;
        backMaterialRef.current.opacity = 1.0;
      }
    } else if (materialPreset === 'gold') {
      materialRef.current.color.setHex(0xfcd34d); // Pure amber gold
      setMetalness(0.96);
      setRoughness(0.14);
      if (pointLightRef.current) pointLightRef.current.color.setHex(0xf59e0b); // Orange laser
      if (backMaterialRef.current) {
        backMaterialRef.current.color.setHex(0x78350f);
        backMaterialRef.current.metalness = 0.95;
        backMaterialRef.current.roughness = 0.2;
        backMaterialRef.current.transparent = false;
        backMaterialRef.current.opacity = 1.0;
      }
    } else if (materialPreset === 'hologram') {
      materialRef.current.color.setHex(0x22d3ee); // Holographic turquoise
      setMetalness(0.2);
      setRoughness(0.7);
      if (pointLightRef.current) pointLightRef.current.color.setHex(0x22d3ee); // Cyan scanner
      if (backMaterialRef.current) {
        backMaterialRef.current.color.setHex(0x0891b2);
        backMaterialRef.current.metalness = 0.1;
        backMaterialRef.current.roughness = 0.1;
        backMaterialRef.current.transparent = true;
        backMaterialRef.current.opacity = 0.35;
      }
    }
  }, [materialPreset]);

  // Handle fine slider updates to WebGL Material
  useEffect(() => {
    if (materialRef.current) {
      materialRef.current.displacementScale = depthScale;
      materialRef.current.metalness = metalness;
      materialRef.current.roughness = roughness;
      if (materialRef.current.normalScale) {
        materialRef.current.normalScale.set(normalStrength, normalStrength);
      }
      materialRef.current.needsUpdate = true;
    }
  }, [depthScale, metalness, roughness, normalStrength]);

  // Visibility toggle helpers
  useEffect(() => {
    if (wireframeMeshRef.current) wireframeMeshRef.current.visible = wireframe;
  }, [wireframe]);

  useEffect(() => {
    if (gridHelperRef.current) gridHelperRef.current.visible = gridVisible;
  }, [gridVisible]);

  useEffect(() => {
    if (laserMeshRef.current) laserMeshRef.current.visible = laserActive;
  }, [laserActive]);

  return (
    <div className="fixed inset-0 bg-slate-950/98 backdrop-blur-xl flex items-center justify-center p-3 sm:p-4 z-55 transition-all animate-fade-in font-sans">
      <div className="bg-slate-900 rounded-3xl overflow-hidden max-w-5xl w-full border border-slate-800/85 shadow-2xl flex flex-col md:flex-row h-[94vh] md:h-[680px]">
        
        {/* LEFT COMPONENT: WebGL VIRTUAL STUDIO */}
        <div className="flex-1 relative bg-slate-950 flex flex-col h-1/2 md:h-full border-b md:border-b-0 md:border-r border-slate-800/90">
          
          {/* Header Dashboard Overlay */}
          <div className="absolute top-4 left-4 right-4 z-10 flex justify-between items-start pointer-events-none gap-2">
            <div className="bg-slate-900/95 border border-slate-800/90 backdrop-blur-md rounded-2xl p-3 flex items-center gap-3 pointer-events-auto shadow-xl">
              <div className="p-2 bg-cyan-950/50 rounded-xl border border-cyan-800/40">
                <Rotate3d className="h-5 w-5 text-cyan-400 animate-spin-slow" />
              </div>
              <div>
                <span className="text-[9px] font-black text-cyan-400 tracking-widest uppercase font-mono block">RECONSTRUCTOR DE IA 3D</span>
                <p className="text-[12px] font-extrabold text-white mt-0.5 max-w-[180px] sm:max-w-[240px] truncate">{title}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 pointer-events-auto">
              <button 
                onClick={() => {
                  setBgThreshold(45);
                  setDepthScale(0.65);
                  setNormalStrength(2.5);
                  setInvertRelief(false);
                  setMetalness(0.92);
                  setRoughness(0.18);
                  setMaterialPreset('steel');
                  setLaserActive(true);
                  setWireframe(false);
                  setGridVisible(true);
                  setRotationSpeed(0.005);
                  setIsAutoRotating(true);
                  setTextureKey(prev => prev + 1);
                }}
                className="bg-slate-900/95 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 text-slate-400 hover:text-white rounded-xl p-2.5 transition-all cursor-pointer shadow-lg flex items-center gap-1.5 text-[10px] font-mono font-bold"
                title="Restablecer calibración 3D"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">RECALIBRAR</span>
              </button>
            </div>
          </div>

          {/* Interactive Live Stage Canvas & AI Loader Overlay */}
          <div className="flex-1 relative w-full h-full">
            {isProcessing && (
              <div className="absolute inset-0 z-20 bg-slate-950 flex flex-col items-center justify-center p-6 text-center select-none">
                {/* Outer Cyber Grid background inside loader */}
                <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#0891b2_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />
                
                {/* Advanced Holographic Ring Spinner */}
                <div className="relative mb-6 flex items-center justify-center">
                  <div className="absolute h-24 w-24 border-2 border-dashed border-cyan-500/20 rounded-full animate-spin-slow"></div>
                  <div className="absolute h-20 w-20 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin"></div>
                  <div className="absolute h-16 w-16 border border-dashed border-cyan-300/15 rounded-full"></div>
                  <div className="p-4 bg-cyan-950/40 rounded-full border border-cyan-800/40">
                    <Cpu className="h-8 w-8 text-cyan-400 animate-pulse" />
                  </div>
                </div>

                <div className="max-w-md w-full space-y-4">
                  <div className="space-y-1">
                    <span className="text-[9px] font-black text-cyan-400 tracking-widest uppercase font-mono block animate-pulse">
                      SÍNTESIS VOLUMÉTRICA POR IA LOGÍSTICA
                    </span>
                    <h4 className="text-sm font-extrabold text-white">Generando Modelo 3D de Mercancía</h4>
                  </div>

                  {/* High-Tech Progress Bar */}
                  <div className="space-y-2">
                    <div className="h-2 w-full bg-slate-900 border border-slate-800 rounded-full overflow-hidden p-0.5">
                      <div 
                        className="h-full bg-gradient-to-r from-cyan-500 via-cyan-400 to-teal-400 rounded-full transition-all duration-100 ease-out shadow-[0_0_10px_rgba(6,182,212,0.5)]"
                        style={{ width: `${aiProgress}%` }}
                      />
                    </div>
                    <div className="flex justify-between items-center text-[10px] font-mono font-bold text-slate-400 px-1">
                      <span>PROCESO: {aiProgress}%</span>
                      <span>WEBGL-2 DETECTOR</span>
                    </div>
                  </div>

                  {/* Pipeline Step List */}
                  <div className="bg-slate-900/60 border border-slate-800/80 rounded-2xl p-3.5 text-left font-mono text-[10.5px] space-y-2.5 max-w-sm mx-auto">
                    {[
                      'Filtro de Segmentación Neuronal',
                      'Extracción de Mapa de Alturas (Luma)',
                      'Síntesis de Textura y Normales Sobel',
                      'Ensamblado de Malla Poligonal 3D'
                    ].map((stepName, idx) => {
                      const isDone = aiProgress >= (idx + 1) * 25;
                      const isActive = aiStep === idx && !isDone;
                      return (
                        <div key={idx} className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 truncate">
                            {isDone ? (
                              <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                            ) : isActive ? (
                              <span className="h-2 w-2 bg-cyan-400 rounded-full animate-ping shrink-0" />
                            ) : (
                              <div className="h-2 w-2 bg-slate-800 rounded-full shrink-0" />
                            )}
                            <span className={isDone ? 'text-slate-400' : isActive ? 'text-cyan-300 font-extrabold' : 'text-slate-600'}>
                              {stepName}
                            </span>
                          </div>
                          {isDone ? (
                            <span className="text-[8px] font-black text-emerald-500 uppercase tracking-wider">Listo</span>
                          ) : isActive ? (
                            <span className="text-[8px] font-black text-cyan-400 uppercase tracking-wider animate-pulse">Procesando</span>
                          ) : (
                            <span className="text-[8px] font-black text-slate-700 uppercase tracking-wider">Espera</span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-[9.5px] font-mono text-cyan-500/80 tracking-wide mt-1 animate-pulse">
                    {aiStatusText}
                  </p>
                </div>
              </div>
            )}
            
            {/* Real ThreeJS container */}
            <div ref={containerRef} className="w-full h-full cursor-grab active:cursor-grabbing" />
          </div>

          {/* Hologram Floating Data HUD (Cyber Look) */}
          <div className="absolute bottom-4 left-4 right-4 z-10 flex flex-wrap items-center justify-between gap-2 pointer-events-none">
            <div className="bg-slate-900/95 border border-slate-800/80 rounded-xl px-3 py-2 flex gap-4 text-[9px] font-mono text-cyan-400 pointer-events-auto shadow-md">
              <div>
                <span className="text-slate-500 font-black mr-1">YAW (G):</span>
                <span>{yaw}°</span>
              </div>
              <div>
                <span className="text-slate-500 font-black mr-1">PITCH (I):</span>
                <span>{pitch}°</span>
              </div>
              <div>
                <span className="text-slate-500 font-black mr-1">DETALLE:</span>
                <span>73K Polígonos</span>
              </div>
            </div>

            <div className="bg-slate-900/95 border border-slate-800/80 rounded-xl px-3 py-2 text-[9px] font-mono text-emerald-400 pointer-events-auto flex items-center gap-1.5 shadow-md">
              <ShieldCheck className="h-3.5 w-3.5" />
              <span className="tracking-wider">AI VOLUMEN GENERADO</span>
            </div>
          </div>

          {/* Quick instructions */}
          <div className="absolute inset-x-0 top-[75%] pointer-events-none opacity-30">
            <p className="text-[10px] text-white font-mono tracking-widest uppercase text-center px-4">
              Arrastra para rotar en 360° • Scroll para Zoom
            </p>
          </div>
        </div>

        {/* RIGHT PANEL: ADVANCED 3D MODIFIERS AND ENGINE CONTROLS */}
        <div className="w-full md:w-[360px] bg-slate-900 flex flex-col h-1/2 md:h-full">
          
          {/* Panel title */}
          <div className="p-4 border-b border-slate-800 flex items-center justify-between bg-slate-950">
            <div className="flex items-center gap-2">
              <Sliders className="h-4 w-4 text-cyan-400" />
              <span className="text-xs font-black text-white uppercase tracking-widest font-mono">CALIBRADOR DE VOLUMEN</span>
            </div>
            <button 
              onClick={onClose}
              className="p-1.5 bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 text-slate-400 hover:text-white rounded-xl cursor-pointer transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Core Configuration Tabs */}
          <div className="flex border-b border-slate-800 text-[10px] font-black font-mono bg-slate-950/50">
            <button
              onClick={() => setActiveTab('material')}
              className={`flex-1 py-3 text-center transition-colors border-b-2 ${activeTab === 'material' ? 'border-cyan-500 text-white bg-slate-900/40' : 'border-transparent text-slate-400 hover:text-slate-300'}`}
            >
              FONDOS Y GROSOR
            </button>
            <button
              onClick={() => setActiveTab('studio')}
              className={`flex-1 py-3 text-center transition-colors border-b-2 ${activeTab === 'studio' ? 'border-cyan-500 text-white bg-slate-900/40' : 'border-transparent text-slate-400 hover:text-slate-300'}`}
            >
              TEXTURAS Y FX
            </button>
            <button
              onClick={() => setActiveTab('metadata')}
              className={`flex-1 py-3 text-center transition-colors border-b-2 ${activeTab === 'metadata' ? 'border-cyan-500 text-white bg-slate-900/40' : 'border-transparent text-slate-400 hover:text-slate-300'}`}
            >
              ANÁLISIS IA
            </button>
          </div>

          {/* Controls Sandbox */}
          <div className="flex-1 p-4 overflow-y-auto space-y-4 bg-slate-900/30">
            
            {activeTab === 'material' && (
              <div className="space-y-4 animate-fade-in">
                
                {/* 1. Background removal threshold (Sensibilidad) */}
                <div className="space-y-2 bg-slate-950/60 p-3 rounded-2xl border border-slate-800/80">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-black text-slate-400 tracking-wider font-mono uppercase">SENSIBILIDAD DE FONDO</span>
                    <span className="text-[10px] font-mono text-cyan-400 font-bold">{bgThreshold} pt</span>
                  </div>
                  <p className="text-[9px] text-slate-400 leading-normal">
                    Regula el filtro inteligente para eliminar perfectamente el fondo de la imagen original.
                  </p>
                  <input
                    type="range"
                    min="5"
                    max="160"
                    value={bgThreshold}
                    onChange={(e) => setBgThreshold(parseInt(e.target.value))}
                    className="w-full accent-cyan-500 bg-slate-800 h-1 rounded-lg cursor-pointer mt-1"
                  />
                  <div className="flex justify-between text-[8px] text-slate-500 font-mono">
                    <span>MÁXIMO DETALLE</span>
                    <span>LIMPIEZA FUERTE</span>
                  </div>
                  <div className="text-[8px] text-cyan-500/90 font-mono flex items-center gap-1.5 pt-1">
                    <Sparkles className="h-3 w-3 animate-pulse" />
                    <span>SUAVIZADO DE BORDES ACTIVO (FEATHER)</span>
                  </div>
                </div>

                {/* 2. Extrusion and displacement scale (Grosor 3D) */}
                <div className="space-y-2 bg-slate-950/60 p-3 rounded-2xl border border-slate-800/80">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-black text-slate-400 tracking-wider font-mono uppercase">GROSOR 3D (DESPLAZAMIENTO)</span>
                    <span className="text-[10px] font-mono text-cyan-400 font-bold">{Math.round(depthScale * 100)}%</span>
                  </div>
                  <p className="text-[9px] text-slate-400 leading-normal">
                    Modifica el nivel de extrusión física y relieve volumétrico sobre la malla WebGL.
                  </p>
                  <input
                    type="range"
                    min="0.05"
                    max="1.5"
                    step="0.05"
                    value={depthScale}
                    onChange={(e) => setDepthScale(parseFloat(e.target.value))}
                    className="w-full accent-cyan-500 bg-slate-800 h-1 rounded-lg cursor-pointer mt-1"
                  />
                  <div className="flex justify-between text-[8px] text-slate-500 font-mono">
                    <span>PLANO</span>
                    <span>EXTRUIDO TOTAL</span>
                  </div>
                </div>

                {/* 3. Invert Relief Heightmap Toggle */}
                <div className="flex items-center justify-between bg-slate-950/60 p-3.5 rounded-2xl border border-slate-800/80 cursor-pointer" onClick={() => setInvertRelief(!invertRelief)}>
                  <div>
                    <span className="text-[10px] font-black text-slate-400 tracking-wider font-mono uppercase block">INVERTIR EXTRUSIÓN</span>
                    <p className="text-[9px] text-slate-500 mt-0.5">Invierte la dirección física del relieve.</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={invertRelief}
                    onChange={() => {}} // Controlled by outer div click
                    className="accent-cyan-500 h-4 w-4 rounded cursor-pointer"
                  />
                </div>

              </div>
            )}

            {activeTab === 'studio' && (
              <div className="space-y-4 animate-fade-in">
                
                {/* 1. Industrial Finishes (Material Presets) */}
                <div className="space-y-2">
                  <span className="text-[9px] font-black text-slate-400 tracking-wider font-mono uppercase block">ACABADO METÁLICO</span>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setMaterialPreset('steel')}
                      className={`p-2.5 border rounded-xl text-left transition-all cursor-pointer ${materialPreset === 'steel' ? 'border-cyan-500 bg-cyan-950/20 text-white' : 'border-slate-800 hover:border-slate-700 text-slate-400'}`}
                    >
                      <Layers className="h-4 w-4 mb-1 text-slate-300" />
                      <p className="text-[10px] font-bold">Acero</p>
                      <p className="text-[8px] text-slate-500 font-mono leading-none mt-0.5">Industrial</p>
                    </button>

                    <button
                      onClick={() => setMaterialPreset('gold')}
                      className={`p-2.5 border rounded-xl text-left transition-all cursor-pointer ${materialPreset === 'gold' ? 'border-amber-500 bg-amber-950/20 text-white' : 'border-slate-800 hover:border-slate-700 text-slate-400'}`}
                    >
                      <Flame className="h-4 w-4 mb-1 text-amber-450 animate-pulse" />
                      <p className="text-[10px] font-bold">Oro Templado</p>
                      <p className="text-[8px] text-slate-500 font-mono leading-none mt-0.5">Brillante</p>
                    </button>

                    <button
                      onClick={() => setMaterialPreset('hologram')}
                      className={`p-2.5 border rounded-xl text-left transition-all cursor-pointer ${materialPreset === 'hologram' ? 'border-cyan-400 bg-slate-950 text-white' : 'border-slate-800 hover:border-slate-700 text-slate-400'}`}
                    >
                      <Wand2 className="h-4 w-4 mb-1 text-cyan-400 animate-pulse" />
                      <p className="text-[10px] font-bold">Holograma</p>
                      <p className="text-[8px] text-slate-500 font-mono leading-none mt-0.5">Control IA</p>
                    </button>
                  </div>
                </div>

                {/* 2. Micro-surface customization */}
                <div className="space-y-3 bg-slate-950/60 p-3 rounded-2xl border border-slate-800/80">
                  <span className="text-[9px] font-black text-slate-400 tracking-wider font-mono uppercase block">CALIDAD SUPERFICIAL</span>
                  
                  {/* Normal Strength */}
                  <div className="space-y-1">
                    <div className="flex justify-between text-[9px] font-mono text-slate-400">
                      <span>CONTRASTE DE RELIEVE (SOBEL)</span>
                      <span className="text-cyan-400">x{normalStrength.toFixed(1)}</span>
                    </div>
                    <input
                      type="range"
                      min="0.5"
                      max="5.0"
                      step="0.1"
                      value={normalStrength}
                      onChange={(e) => setNormalStrength(parseFloat(e.target.value))}
                      className="w-full accent-cyan-500 bg-slate-800 h-1 rounded"
                    />
                  </div>

                  {/* Metalness */}
                  <div className="space-y-1 pt-1">
                    <div className="flex justify-between text-[9px] font-mono text-slate-400">
                      <span>BRILLO METÁLICO</span>
                      <span className="text-cyan-400">{Math.round(metalness * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min="0.0"
                      max="1.0"
                      step="0.05"
                      value={metalness}
                      onChange={(e) => setMetalness(parseFloat(e.target.value))}
                      className="w-full accent-cyan-500 bg-slate-800 h-1 rounded"
                    />
                  </div>

                  {/* Roughness */}
                  <div className="space-y-1 pt-1">
                    <div className="flex justify-between text-[9px] font-mono text-slate-400">
                      <span>RUGOSIDAD (MATE)</span>
                      <span className="text-cyan-400">{Math.round(roughness * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min="0.0"
                      max="1.0"
                      step="0.05"
                      value={roughness}
                      onChange={(e) => setRoughness(parseFloat(e.target.value))}
                      className="w-full accent-cyan-500 bg-slate-800 h-1 rounded"
                    />
                  </div>
                </div>

                {/* 3. Auto rotation controls */}
                <div className="space-y-2 bg-slate-950/60 p-3 rounded-2xl border border-slate-800/80">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[10px] font-black text-slate-400 tracking-wider font-mono uppercase block">ROTOR AUTOMÁTICO</span>
                      <p className="text-[8px] text-slate-500">Giro orbital autónomo continuo.</p>
                    </div>
                    <button
                      onClick={() => setIsAutoRotating(!isAutoRotating)}
                      className={`p-1.5 rounded-xl transition-all cursor-pointer ${isAutoRotating ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-slate-800 text-slate-500 border border-slate-700'}`}
                    >
                      {isAutoRotating ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* 4. Cyber laser scanner & wireframe */}
                <div className="space-y-2.5 bg-slate-950/60 p-3 rounded-2xl border border-slate-800/80">
                  <span className="text-[9px] font-black text-slate-400 tracking-wider font-mono uppercase block">SOPORTES DE ESCENARIO</span>
                  
                  <label className="flex items-center justify-between cursor-pointer select-none">
                    <span className="text-[10.5px] font-bold text-slate-300">Escáner Láser Oscilante</span>
                    <input
                      type="checkbox"
                      checked={laserActive}
                      onChange={(e) => setLaserActive(e.target.checked)}
                      className="accent-cyan-500 h-3.5 w-3.5 rounded cursor-pointer"
                    />
                  </label>

                  <label className="flex items-center justify-between cursor-pointer select-none">
                    <span className="text-[10.5px] font-bold text-slate-300">Malla Estructural (Wireframe)</span>
                    <input
                      type="checkbox"
                      checked={wireframe}
                      onChange={(e) => setWireframe(e.target.checked)}
                      className="accent-cyan-500 h-3.5 w-3.5 rounded cursor-pointer"
                    />
                  </label>

                  <label className="flex items-center justify-between cursor-pointer select-none">
                    <span className="text-[10.5px] font-bold text-slate-300">Rejilla de Referencia</span>
                    <input
                      type="checkbox"
                      checked={gridVisible}
                      onChange={(e) => setGridVisible(e.target.checked)}
                      className="accent-cyan-500 h-3.5 w-3.5 rounded cursor-pointer"
                    />
                  </label>
                </div>

              </div>
            )}

            {activeTab === 'metadata' && (
              <div className="space-y-3 animate-fade-in text-xs">
                
                {/* 3D Texture analysis */}
                <div className="bg-slate-950/60 p-3 rounded-2xl border border-slate-800/80 space-y-3 font-mono text-[10.5px]">
                  <div>
                    <span className="text-slate-500 uppercase text-[9px] block font-black">POLÍGONOS GENERADOS</span>
                    <span className="text-cyan-400 font-extrabold">73,728 Caras</span>
                  </div>
                  <div>
                    <span className="text-slate-500 uppercase text-[9px] block font-black">MÉTODO DE VOLUMEN</span>
                    <span className="text-white font-bold">Extrusión Dinámica por Desplazamiento</span>
                  </div>
                  <div>
                    <span className="text-slate-500 uppercase text-[9px] block font-black">MAPA DE NORMALES</span>
                    <span className="text-emerald-400 font-bold">Algoritmo de Sobel (Matriz 3x3)</span>
                  </div>
                  <div>
                    <span className="text-slate-500 uppercase text-[9px] block font-black">ESTRUCTURA DE SOPORTE</span>
                    <span className="text-white font-bold">Carcasa 3D de Estado Sólido integrada</span>
                  </div>
                </div>

                <div className="p-3 bg-cyan-950/20 border border-cyan-800/40 rounded-2xl text-[10px] text-cyan-400/90 leading-relaxed space-y-1.5">
                  <div className="flex items-center gap-1">
                    <Info className="h-3.5 w-3.5" />
                    <span className="font-bold uppercase font-mono">RECONSTRUCCIÓN DE IA:</span>
                  </div>
                  <p className="font-sans text-slate-300">
                    El motor 3D analiza de forma inteligente la silueta y tonos de la imagen para aislar el producto. Luego calcula dinámicamente un mapa de alturas y normales, y añade una carcasa de soporte metálica posterior, proporcionando una pieza sólida totalmente tridimensional.
                  </p>
                </div>
              </div>
            )}

          </div>

          {/* Clean Footer */}
          <div className="p-4 border-t border-slate-800 text-[9px] text-slate-500 text-center font-mono bg-slate-950/90 tracking-widest uppercase">
            SISTEMA LOGÍSTICO IA • ESTUDIO DE VOLUMEN
          </div>
        </div>

      </div>
    </div>
  );
}
